/**
 * Манифест атласов спрайтов: какие картинки грузить, где в них кадры, какие кадры складываются в анимации
 * и где у кадра точка привязки (якорь). Пишет конвейер `tools/voxel` (A1; пока — заглушка
 * `tools/voxel/placeholder`), читает рендер (C1) и сервер для картинок шаринга (S4).
 *
 * Имена:
 * - кадр — `<entity>/<anim>/<dir>/<n>` или `<entity>/<variant>` для статичных (`tile/grass`);
 * - анимация — `<entity>/<anim>/<dir>` (`zombie.walker/walk/se`), см. {@link animationKey};
 * - entity: `hero.<HeroId>`, `zombie.<ZombieKind>`, `boss.<bossId>`, `hq`, `cannon`, `platform`, `tile`, `prop`,
 *   `projectile.<ProjectileKind>`, `fx.<EffectKind>`, `coin`, `ui`.
 *
 * Якорь — точка кадра в пикселях от левого верхнего угла, которая совпадает с позицией сущности на поле
 * (у героя и зомби — середина ступней). Рендер ставит спрайт так, чтобы якорь лёг в экранную точку `pos`.
 */
import { z } from "zod";
import type { Direction } from "./common";
import { PosInt, UInt } from "./common";

/** Версия формата манифеста. */
export const ATLAS_MANIFEST_FORMAT = 1;

/** Одна картинка атласа. */
export const AtlasImage = z.object({
  /** Имя файла рядом с манифестом (`base-0.png`). */
  file: z.string().regex(/^[a-z0-9_.-]+\.(png|webp)$/),
  width: PosInt,
  height: PosInt,
  /** sha256 файла (hex) — CI сверяет с пересобранным. */
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  /**
   * Та же картинка в lossless WebP — её грузит браузер (ISSUE-85). Дописывает сборка клиента в `dist`, в репозитории
   * нет; сервер (картинка шаринга) и сверка CI читают `file`.
   */
  webp: z
    .string()
    .regex(/^[a-z0-9_.-]+\.webp$/)
    .optional(),
});
export type AtlasImage = z.infer<typeof AtlasImage>;

/** Имя кадра или анимации. */
export const SpriteName = z.string().regex(/^[a-zA-Z0-9_.]+(\/[a-zA-Z0-9_.]+)*$/);

/** Кадр в атласе. */
export const AtlasFrame = z.object({
  /** Индекс картинки в {@link AtlasManifest.images}. */
  image: UInt,
  x: UInt,
  y: UInt,
  w: PosInt,
  h: PosInt,
  /** Якорь в пикселях кадра. */
  anchor: z.object({ x: z.number().int(), y: z.number().int() }),
});
export type AtlasFrame = z.infer<typeof AtlasFrame>;

/** Анимация: последовательность кадров. */
export const AtlasAnimation = z.object({
  /** Имена кадров из {@link AtlasManifest.frames} (кадр может повторяться и встречаться в разных анимациях). */
  frames: z.array(SpriteName).min(1),
  /** Кадров в секунду. */
  fps: PosInt,
  /** Зациклена (ходьба, покой) или играет один раз и стоит на последнем кадре (смерть, выстрел). */
  loop: z.boolean(),
});
export type AtlasAnimation = z.infer<typeof AtlasAnimation>;

/** Манифест набора атласов. */
export const AtlasManifest = z
  .object({
    format: z.literal(ATLAS_MANIFEST_FORMAT),
    /** Кто собрал: `placeholder` — цветные кубы, `voxel` — рендер Blender. */
    generator: z.enum(["placeholder", "voxel"]),
    /** sha256 исходников (модели + скрипт), из которых собран атлас. */
    sourceHash: z.string().regex(/^[0-9a-f]{64}$/),
    /** Пикселей экрана на клетку поля при масштабе 1 (ширина ромба клетки в изометрии). */
    tilePixels: PosInt,
    images: z.array(AtlasImage).min(1),
    frames: z.record(SpriteName, AtlasFrame),
    animations: z.record(SpriteName, AtlasAnimation),
  })
  .superRefine((m, ctx) => {
    for (const [name, f] of Object.entries(m.frames)) {
      const img = m.images[f.image];
      if (!img) {
        ctx.addIssue({ code: "custom", message: `frame ${name}: no image ${f.image}` });
        continue;
      }
      if (f.x + f.w > img.width || f.y + f.h > img.height) {
        ctx.addIssue({ code: "custom", message: `frame ${name}: outside image` });
      }
    }
    for (const [name, a] of Object.entries(m.animations)) {
      for (const fr of a.frames) {
        if (!(fr in m.frames)) ctx.addIssue({ code: "custom", message: `animation ${name}: unknown frame ${fr}` });
      }
    }
  });
export type AtlasManifest = z.infer<typeof AtlasManifest>;

/** Ключ анимации: `animationKey("zombie.walker", "walk", "se")` → `zombie.walker/walk/se`. */
export function animationKey(entity: string, anim: string, dir: Direction): string {
  return `${entity}/${anim}/${dir}`;
}

/**
 * Обязательные анимации, которые должен содержать любой атлас v1 (заглушка и настоящий). Рендер вправе на них
 * рассчитывать. Направления — все 8 для героев и зомби; статичные сущности — направление `s`.
 */
export const REQUIRED_ENTITIES = {
  heroes: { entities: ["gunner", "bomber", "sparky", "blaze", "frosty"], anims: ["idle", "shoot", "skill"] },
  zombies: { entities: ["walker", "runner", "brute", "shield"], anims: ["walk", "attack", "die"] },
} as const;
